import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { getBets, getTransactions } from '@App/api/reports'

import { Order, Trade } from '@Redux/slices/trade/trades/types'

export interface MoneyObject {
  amount: number
  currency: string
  rate_change: number
  currency_symbol: string
  system_amount?: number
  system_currency?: string
  system_symbol?: string
  display?: boolean
}

export interface DateTimeJson {
  date: string
  timezone_type: number
  timezone: string
}

export interface TransactionSource {
  id: number
  operation_type: string
  rate_change_id: number
  amount: MoneyObject
  operation_time: string
  operation_note: string | null
  payment_operation_params: unknown
}

export interface TransactionDestination {
  id: number
  operation_type: string
  amount: MoneyObject
  operation_time: DateTimeJson | string
  operation_note: string | null
  payment_operation_params: unknown
  external_id?: string | null
  bet_id?: number
}

export interface UserWallet {
  id: number
  user_id: number
  name: string
  balance: MoneyObject & { system_amount: number; display: boolean }
  front_balance: MoneyObject
  wallet_type: string
  non_transaction: boolean
  currency_symbol: string
  update_time: DateTimeJson
  ut_ts: number
  type: string
  mirror_trade: boolean
  trading_settings: Record<string, number>
  wallet_id?: number
  bonus_instance_id?: number | null
}

export interface MiniUser {
  id: number
  username: string
  default_currency: string
  mobile_phone: string
  email: string
  language: string
  wallets: UserWallet[] | null

  registration_time: DateTimeJson
  last_activity_time: DateTimeJson
  last_login_time: DateTimeJson

  ip: string | null
  country: string
  front_id: string

  [key: string]: unknown
}

export interface Transaction {
  id: number
  front_id: string
  transaction_time: string // "2025-12-05 08:55:15"
  type: string
  amount: string
  tax: string

  status: string

  source_type: string
  destination_type: string

  provider: string

  initiator_id: number
  user_id: number

  source: TransactionSource
  destination: TransactionDestination

  initiator_user: MiniUser

  ip: string | null
  note: string | null
  device_id: string | null
  title: string | null
  approve_username: string | null

  exchange_transaction: number
  player_affiliate_id: number | null
}

const isTransaction = (transaction: unknown): transaction is Transaction => {
  return !!(transaction && typeof transaction === 'object' && 'transaction_time' in transaction)

}

const isTransactionArray = (transactions: unknown): transactions is Transaction[] => {
  if (!Array.isArray(transactions)) return false
  if (!transactions.length) return true
  return !!transactions.every(isTransaction)

}

const isTradeArray = (trades: unknown): trades is Trade[] => {
  if (!Array.isArray(trades)) return false
  return !!trades.length

}

const isOrderArray = (orders: unknown): orders is Order[] => {
  if (!Array.isArray(orders)) return false
  return !!orders.length

}

const initialState: {
  transactions: Transaction[]
  trades: Trade[]
  orders: Order[]
} = {
  transactions: [],
  trades: [],
  orders: [],
}

export const asyncGetTransactions = createAsyncThunk('reports/transactions', async (params: any) => {
  const resp = await getTransactions()
  if (typeof resp !== 'object' || resp == null) {
    return []
  }

  if ('data' in resp && isTransactionArray(resp.data)) {
    console.log(resp.data)
    return resp.data
  }
  return []
})

export const asyncGetTrades = createAsyncThunk('reports/trades', async (accountId: string) => {
  const resp = await getBets('trades', accountId)
  if (typeof resp !== 'object' || resp == null) {
    return []
  }

  if ('data' in resp && isTradeArray(resp.data)) {
    return resp.data
  }
  return []
})

export const asyncGetOrders = createAsyncThunk('reports/orders', async (accountId: string) => {
  const resp = await getBets('orders', accountId)
  if (typeof resp !== 'object' || resp == null) {
    return []
  }

  if ('data' in resp && isOrderArray(resp.data)) {
    return resp.data
  }

  return []
})

export const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(asyncGetTransactions.fulfilled, (state, action) => {
        state.transactions = action.payload
      })
      .addCase(asyncGetTrades.fulfilled, (state, { payload }) => {
        state.trades = payload ?? []
      })
      .addCase(asyncGetOrders.fulfilled, (state, { payload }) => {
        state.orders = payload ?? []
      })
  },
})

export default reportsSlice.reducer
